// Yeni stok sayımı / taslak düzenleme: depo, kapsam (tam/kısmi), kör sayım, tolerans, plan ve sayanlar.
import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert, Autocomplete, Box, Button, Checkbox, Chip, Dialog, DialogActions, DialogContent, DialogTitle,
  FormControlLabel, MenuItem, Paper, Switch, TextField, ToggleButton, ToggleButtonGroup, Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../../../contexts/AuthContext';
import { WarehouseService, type InventoryWarehouseDto } from '../services/warehouseService';
import { CategoryService, type InventoryCategoryDto } from '../services/categoryService';
import { BrandService } from '../services/brandService';
import { getPersonnel } from '../../../../services/migration/personnelDataService';
import { stockCountService, type StockCountDetail, type StockCountType } from './stockCountService';

interface Person { uid: string; name: string; role?: string }
interface AssigneeRow extends Person { allowManualEntry: boolean }

interface Props {
  open: boolean;
  onClose: () => void;
  onSaved: (id: string) => void;
  /** Verilirse taslak düzenlenir. */
  existing?: StockCountDetail | null;
  warehouseFilter?: (w: InventoryWarehouseDto) => boolean;
}

const toLocalInput = (iso?: string | null) => {
  if (!iso) return '';
  const d = new Date(iso.endsWith('Z') || iso.includes('+') ? iso : `${iso}Z`);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};
const fromLocalInput = (v: string) => (v ? new Date(v).toISOString() : null);
const parseNum = (v: string) => {
  const n = Number(v.replace(',', '.'));
  return v.trim() === '' || !Number.isFinite(n) ? null : n;
};

const NewStockCountDialog: React.FC<Props> = ({ open, onClose, onSaved, existing, warehouseFilter }) => {
  const { i18n } = useTranslation();
  const tt = (tr: string, en: string) => ((i18n.language || '').startsWith('en') ? en : tr);
  const { userDetails } = useAuth() as any;
  const companyId: string = userDetails?.companyId || '';

  const [warehouses, setWarehouses] = useState<InventoryWarehouseDto[]>([]);
  const [categories, setCategories] = useState<InventoryCategoryDto[]>([]);
  const [brands, setBrands] = useState<{ id: string; name: string }[]>([]);
  const [people, setPeople] = useState<Person[]>([]);

  const [warehouseId, setWarehouseId] = useState('');
  const [name, setName] = useState('');
  const [type, setType] = useState<StockCountType>('PARTIAL');
  const [categoryIds, setCategoryIds] = useState<string[]>([]);
  const [brandIds, setBrandIds] = useState<string[]>([]);
  const [includeZeroStock, setIncludeZeroStock] = useState(false);
  const [includeInactive, setIncludeInactive] = useState(false);
  const [isBlind, setIsBlind] = useState(true);
  const [tolPct, setTolPct] = useState('');
  const [tolAmt, setTolAmt] = useState('');
  const [plannedStart, setPlannedStart] = useState('');
  const [plannedEnd, setPlannedEnd] = useState('');
  const [notes, setNotes] = useState('');
  const [assignees, setAssignees] = useState<AssigneeRow[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open || !companyId) return;
    WarehouseService.getAllWarehouses(companyId)
      .then((list) => {
        const normal = list.filter((w) => (w.warehouseType || 'Normal') !== 'Smart' && (!warehouseFilter || warehouseFilter(w)));
        setWarehouses(normal);
        if (!existing && normal.length === 1) setWarehouseId(normal[0].id);
      })
      .catch(() => setWarehouses([]));
    CategoryService.getAllCategories(companyId).then(setCategories).catch(() => setCategories([]));
    BrandService.getAllBrands(companyId).then((b: any[]) => setBrands(b.map((x) => ({ id: x.id, name: x.name })))).catch(() => setBrands([]));
    getPersonnel(companyId)
      .then((list: any[]) => setPeople(list
        .filter((p) => p?.id && !p?.isDeleted)
        .map((p) => ({ uid: String(p.id), name: String(p.name || p.email || p.id), role: p.role }))))
      .catch(() => setPeople([]));
  }, [open, companyId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!open) return;
    setError(null);
    if (existing) {
      setWarehouseId(existing.warehouseId);
      setName(existing.name || '');
      setType(existing.type === 'FULL' ? 'FULL' : 'PARTIAL');
      setCategoryIds(existing.scope.categoryIds || []);
      setBrandIds(existing.scope.brandIds || []);
      setIncludeZeroStock(existing.scope.includeZeroStock);
      setIncludeInactive(existing.scope.includeInactive);
      setIsBlind(existing.isBlind);
      setTolPct(existing.tolerancePercent != null ? String(existing.tolerancePercent) : '');
      setTolAmt(existing.toleranceAmount != null ? String(existing.toleranceAmount) : '');
      setPlannedStart(toLocalInput(existing.plannedStartAt));
      setPlannedEnd(toLocalInput(existing.plannedEndAt));
      setNotes(existing.notes || '');
      setAssignees(existing.assignees.map((a) => ({
        uid: a.userUid, name: a.userName || a.userUid, role: a.role || undefined, allowManualEntry: a.allowManualEntry,
      })));
    } else {
      setName(''); setType('PARTIAL'); setCategoryIds([]); setBrandIds([]); setIncludeZeroStock(false);
      setIncludeInactive(false); setIsBlind(true); setTolPct(''); setTolAmt(''); setPlannedStart('');
      setPlannedEnd(''); setNotes(''); setAssignees([]);
    }
  }, [open, existing]);

  // Seçilen kişi için son "elle giriş" tercihi hatırlanır.
  const rememberedManual = (uid: string): boolean => {
    try { return localStorage.getItem(`stockCount.allowManual.${uid}`) === '1'; } catch { return false; }
  };
  const rememberManual = (uid: string, value: boolean) => {
    try { localStorage.setItem(`stockCount.allowManual.${uid}`, value ? '1' : '0'); } catch { /* yok say */ }
  };

  const selectedPeople = useMemo(() => people.filter((p) => assignees.some((a) => a.uid === p.uid)), [people, assignees]);
  const categoryName = (id: string) => categories.find((c) => c.id === id)?.name || id;
  const brandName = (id: string) => brands.find((b) => b.id === id)?.name || id;

  const save = async (startNow: boolean) => {
    setError(null);
    if (!warehouseId) { setError(tt('Depo seçin.', 'Select a warehouse.')); return; }
    if (type === 'PARTIAL' && categoryIds.length === 0 && brandIds.length === 0) {
      setError(tt('Kısmi sayım için en az bir kategori ya da marka seçin.', 'Select at least one category or brand for a partial count.'));
      return;
    }
    setSaving(true);
    try {
      const input = {
        name: name.trim() || undefined,
        type,
        warehouseId,
        scope: { categoryIds, brandIds, itemIds: [], includeZeroStock, includeInactive },
        isBlind,
        tolerancePercent: parseNum(tolPct),
        toleranceAmount: parseNum(tolAmt),
        plannedStartAt: fromLocalInput(plannedStart),
        plannedEndAt: fromLocalInput(plannedEnd),
        notes: notes.trim() || undefined,
        assignees: assignees.map((a) => ({ userUid: a.uid, userName: a.name, role: a.role || 'personnel', allowManualEntry: a.allowManualEntry })),
        startNow,
      };
      const saved = existing ? await stockCountService.update(existing.id, input) : await stockCountService.create(input);
      onSaved(saved.id);
    } catch (e: any) {
      setError(e?.message || tt('Sayım kaydedilemedi.', 'Count could not be saved.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>
        {existing ? `${existing.number} · ${tt('Taslağı düzenle', 'Edit draft')}` : tt('Yeni stok sayımı', 'New stock count')}
      </DialogTitle>
      <DialogContent dividers sx={{ display: 'grid', gap: 2 }}>
        {error && <Alert severity="error">{error}</Alert>}

        <Typography variant="overline" color="text.secondary">{tt('Nerede', 'Where')}</Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 1.5, mt: -1.5 }}>
          <TextField select size="small" label={tt('Depo', 'Warehouse')} required value={warehouseId} onChange={(e) => setWarehouseId(e.target.value)}
            helperText={tt('Akıllı (WMS) depolar WMS sayımıyla sayılır.', 'Smart (WMS) warehouses use WMS counts.')}>
            {warehouses.map((w) => <MenuItem key={w.id} value={w.id}>{w.name}</MenuItem>)}
          </TextField>
          <TextField size="small" label={tt('Sayım adı', 'Count name')} placeholder={tt('Örn. İçecek rafı', 'e.g. Drinks shelf')} value={name} onChange={(e) => setName(e.target.value)} />
        </Box>

        <Typography variant="overline" color="text.secondary">{tt('Ne sayılacak', 'What to count')}</Typography>
        <Box sx={{ mt: -1.5, display: 'grid', gap: 1.5 }}>
          <ToggleButtonGroup exclusive size="small" value={type} onChange={(_, v) => v && setType(v)}>
            <ToggleButton value="FULL">{tt('Tam sayım (depodaki her ürün)', 'Full count (every product)')}</ToggleButton>
            <ToggleButton value="PARTIAL">{tt('Kısmi sayım', 'Partial count')}</ToggleButton>
          </ToggleButtonGroup>
          {type === 'PARTIAL' && (
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 1.5 }}>
              <Autocomplete
                multiple size="small" options={categories.map((c) => c.id)} value={categoryIds}
                onChange={(_, v) => setCategoryIds(v)} getOptionLabel={categoryName}
                renderTags={(v, getProps) => v.map((id, i) => <Chip size="small" label={categoryName(id)} {...getProps({ index: i })} key={id} />)}
                renderInput={(p) => <TextField {...p} label={tt('Kategoriler (alt kategoriler dahil)', 'Categories (incl. subcategories)')} />}
              />
              <Autocomplete
                multiple size="small" options={brands.map((b) => b.id)} value={brandIds}
                onChange={(_, v) => setBrandIds(v)} getOptionLabel={brandName}
                renderTags={(v, getProps) => v.map((id, i) => <Chip size="small" label={brandName(id)} {...getProps({ index: i })} key={id} />)}
                renderInput={(p) => <TextField {...p} label={tt('Markalar', 'Brands')} />}
              />
            </Box>
          )}
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <FormControlLabel control={<Checkbox checked={includeZeroStock} onChange={(e) => setIncludeZeroStock(e.target.checked)} />}
              label={tt('Stoğu sıfır olanları da listele', 'Include zero-stock products')} />
            <FormControlLabel control={<Checkbox checked={includeInactive} onChange={(e) => setIncludeInactive(e.target.checked)} />}
              label={tt('Pasif ürünleri dahil et', 'Include inactive products')} />
          </Box>
        </Box>

        <Typography variant="overline" color="text.secondary">{tt('Nasıl sayılacak', 'How to count')}</Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '2fr 1fr 1fr' }, gap: 1.5, mt: -1.5, alignItems: 'center' }}>
          <FormControlLabel control={<Switch checked={isBlind} onChange={(e) => setIsBlind(e.target.checked)} />}
            label={tt('Kör sayım (sayan beklenen miktarı görmez; personel için her zaman)', 'Blind count (counters do not see expected qty; always for staff)')} />
          <TextField size="small" label={tt('Tolerans %', 'Tolerance %')} value={tolPct} onChange={(e) => setTolPct(e.target.value)} inputProps={{ inputMode: 'decimal' }} />
          <TextField size="small" label={tt('Tolerans ₺', 'Tolerance ₺')} value={tolAmt} onChange={(e) => setTolAmt(e.target.value)} inputProps={{ inputMode: 'decimal' }} />
        </Box>

        <Typography variant="overline" color="text.secondary">{tt('Ne zaman, kim', 'When, who')}</Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 1.5, mt: -1.5 }}>
          <TextField size="small" type="datetime-local" label={tt('Planlanan başlangıç', 'Planned start')} InputLabelProps={{ shrink: true }}
            value={plannedStart} onChange={(e) => setPlannedStart(e.target.value)} />
          <TextField size="small" type="datetime-local" label={tt('Planlanan bitiş', 'Planned end')} InputLabelProps={{ shrink: true }}
            value={plannedEnd} onChange={(e) => setPlannedEnd(e.target.value)} />
        </Box>
        <Autocomplete
          multiple size="small" options={people} value={selectedPeople}
          isOptionEqualToValue={(a, b) => a.uid === b.uid}
          getOptionLabel={(p) => p.name}
          onChange={(_, v) => setAssignees(v.map((p) => assignees.find((a) => a.uid === p.uid)
            ?? { ...p, allowManualEntry: rememberedManual(p.uid) }))}
          renderInput={(p) => <TextField {...p} label={tt('Sayacak kişiler', 'Counters')} helperText={tt('Atanan kişinin mobil ana ekranında "Sayım Görevlerim" çıkar.', 'Assigned people see it under "My Count Tasks" in the app.')} />}
        />
        {assignees.length > 0 && (
          <Box sx={{ display: 'grid', gap: 1 }}>
            {assignees.map((a) => (
              <Paper key={a.uid} variant="outlined" sx={{ px: 1.5, py: 0.75, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <Typography variant="body2" fontWeight={600} sx={{ flex: 1 }}>{a.name}</Typography>
                <FormControlLabel
                  control={(
                    <Switch
                      size="small"
                      checked={a.allowManualEntry}
                      onChange={(e) => {
                        rememberManual(a.uid, e.target.checked);
                        setAssignees((prev) => prev.map((x) => (x.uid === a.uid ? { ...x, allowManualEntry: e.target.checked } : x)));
                      }}
                    />
                  )}
                  label={a.allowManualEntry
                    ? tt('Elle miktar girebilir', 'Can type quantities')
                    : tt('Elle miktar girişi kapalı · yalnız okutma', 'Scan only')}
                />
              </Paper>
            ))}
            <Typography variant="caption" color="text.secondary">
              {tt('"Elle miktar girişi" kapalı kişi barkodlu adetli ürünleri yalnız okutarak sayar; barkodsuz ve kg ile satılan ürünler her durumda elle girilir.',
                'With manual entry off, barcoded unit items can only be scanned; items without barcode or sold by weight are always typed.')}
            </Typography>
          </Box>
        )}
        <TextField size="small" label={tt('Not', 'Note')} value={notes} onChange={(e) => setNotes(e.target.value)} multiline minRows={2} />
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 1.5 }}>
        <Button onClick={onClose} disabled={saving}>{tt('Vazgeç', 'Cancel')}</Button>
        <Box sx={{ flex: 1 }} />
        <Button variant="outlined" onClick={() => save(false)} disabled={saving}>{tt('Taslak olarak kaydet', 'Save as draft')}</Button>
        <Button variant="contained" onClick={() => save(true)} disabled={saving}>{tt('Kaydet ve sayımı başlat', 'Save and start')}</Button>
      </DialogActions>
    </Dialog>
  );
};

export default NewStockCountDialog;
